/**
 * Downtime data transformation and manipulation utility functions
 * Used by downtime pages
 */
import { getDateOffset } from "@/lib/datetime-utils.js";

/**
 * True when downtime summary `assetId` (non-nullable int from the downtime API) equals
 * fleet / route `asset.id` (string or number from the UI).
 * @param {number} downtimeAssetId
 * @param {unknown} fleetOrRouteAssetId
 * @returns {boolean}
 */
export function sameDowntimeAssetPk(downtimeAssetId, fleetOrRouteAssetId) {
  return Number(downtimeAssetId) === Number(fleetOrRouteAssetId);
}

/**
 * Enrich downtime events with asset information
 * @param {Array} downtimeEvents - Array of downtime events
 * @param {Array} assets - Array of assets from AssetContext
 * @returns {Array} Enriched downtime events array
 */
export const enrichDowntimeEventsWithAssetData = (downtimeEvents, assets) => {
  if (!Array.isArray(downtimeEvents) || !Array.isArray(assets)) {
    return downtimeEvents;
  }

  const assetMapById = assets.reduce((acc, asset) => {
    acc[String(asset.id)] = asset;
    return acc;
  }, {});

  return downtimeEvents.map((downtimeEvent) => {
    const asset = assetMapById[String(downtimeEvent.assetId)];
    if (asset) {
      return {
        ...downtimeEvent,
        assetNumber: asset.assetNumber,
        assetLocation: asset.location,
        assetLease: asset.lease,
        status: asset.status,
        vendor: asset.vendor,
      };
    }
    return downtimeEvent;
  });
};

const DowntimeIsMechanical = {
  0: null,
  1: true,
  2: false,
};

/**
 * Calculate downtime hours for an event within a date range
 * Can work with either API event format (ReportingDate/ClosingDate) or transformed event format (dateTime/dateTimeEnd)
 * @param {Object} event - Event object (either API format with ReportingDate/ClosingDate/DowntimeHours, or transformed with dateTime/dateTimeEnd/dtHours)
 * @param {Date} rangeStartDate - Start date of the filter range (required)
 * @param {Date} rangeEndDate - End date of the filter range (required)
 * @returns {number} Calculated downtime hours in the range
 */
export const calculateDowntimeHoursInRange = (
  event,
  rangeStartDate,
  rangeEndDate
) => {
  // Support both API event format and transformed event format
  const reportingDate = event.ReportingDate
    ? new Date(event.ReportingDate)
    : event.dateTime
      ? new Date(event.dateTime)
      : null;

  if (!reportingDate || isNaN(reportingDate.getTime())) {
    return 0;
  }

  // Support both formats for closing date
  // For transformed events, check if it's open via the 'active' field or by comparing dates
  let closingDate = null;
  if (event.ClosingDate) {
    closingDate = new Date(event.ClosingDate);
  } else if (event.dateTimeEnd) {
    const eventEndDate = new Date(event.dateTimeEnd);
    const eventStartDate = new Date(event.dateTime);
    // If the event has an 'active' field, use it. Otherwise, check if dateTimeEnd significantly differs from dateTime
    // For open events, dateTimeEnd might be set to a default value close to dateTime
    if (
      event.active === true
      || (eventStartDate
        && Math.abs(eventEndDate.getTime() - eventStartDate.getTime()) < 1000)
    ) {
      closingDate = null; // Open event
    } else {
      closingDate = eventEndDate;
    }
  }

  const isOpenEvent = !closingDate;

  // If event ends before range starts, no hours in range
  if (closingDate && closingDate < rangeStartDate) {
    return 0;
  }

  // If event starts after range ends, no hours in range
  if (reportingDate > rangeEndDate) {
    return 0;
  }

  // Calculate the intersection of event duration with date range
  // Clamp event start to the range: use the later of event start or range start
  let eventStartInRange =
    reportingDate < rangeStartDate ? rangeStartDate : reportingDate;

  // Calculate the effective end: for open events use current time, for closed events use closing date
  // Then clamp to the range end date
  const now = new Date();
  const eventEnd = isOpenEvent ? now : closingDate;

  // Clamp event end to the range: use the earlier of event end or range end
  // For ongoing events, also limit to current time
  let eventEndInRange;
  if (isOpenEvent) {
    // For open events: use the minimum of (now, range end)
    eventEndInRange = now < rangeEndDate ? now : rangeEndDate;
  } else {
    // For closed events: use the minimum of (event end, range end)
    eventEndInRange = eventEnd < rangeEndDate ? eventEnd : rangeEndDate;
  }

  // Calculate hours in the intersection
  const millisecondsInRange =
    eventEndInRange.getTime() - eventStartInRange.getTime();
  const hoursInRange = millisecondsInRange / (1000 * 60 * 60);

  // If hours is 0 or negative, return 0 (event doesn't overlap with range meaningfully)
  if (hoursInRange <= 0) {
    return 0;
  }

  // For open events, return the calculated hours from reportingDate to range end
  if (isOpenEvent) {
    return hoursInRange;
  }

  // For closed events, check if event is completely within range
  // If so, try to use original DowntimeHours if available; otherwise use calculated intersection hours
  const eventCompletelyInRange =
    reportingDate >= rangeStartDate
    && closingDate
    && closingDate <= rangeEndDate;

  if (eventCompletelyInRange) {
    // For transformed events, use dtHours if available and reasonable
    if (event.dtHours != null && event.dtHours >= 0) {
      // Only use original if it's close to our calculated value (within 0.1 hour tolerance)
      // This handles rounding differences
      if (Math.abs(event.dtHours - hoursInRange) < 0.1) {
        return event.dtHours;
      }
    }
    // For API format events, use DowntimeHours
    if (event.DowntimeHours != null && event.DowntimeHours >= 0) {
      return event.DowntimeHours;
    }
  }

  return hoursInRange;
};

/**
 * Transform the `getAllDowntimeEvents` response into UI summary rows (see `downtime-api.js` typedefs).
 * Each `apiResponse.DowntimeEvents[]` item matches `DowntimeEventAndAssetSummaryResponseDTO`: non-nullable int `AssetId`
 * plus nested `DowntimeEvents`. Display `assetNumber` is filled by {@link enrichDowntimeEventsWithAssetData}.
 * @param {Object|null|undefined} apiResponse - `GetAllDowntimeEventsResponse` shape from the XT endpoint
 * @param {Date} startDate - Start date of the filter range (required)
 * @param {Date} endDate - End date of the filter range (required)
 * @returns {Array} Transformed downtime events array
 */
export const transformApiResponseToDowntimeEventSummaries = (
  apiResponse,
  startDate,
  endDate
) => {
  // Safety checks
  if (!apiResponse || typeof apiResponse !== "object") {
    return [];
  }

  if (!Array.isArray(apiResponse.DowntimeEvents)) {
    return [];
  }

  const groupedByAsset = apiResponse.DowntimeEvents.reduce((acc, assetData) => {
    if (!assetData || typeof assetData !== "object") {
      return acc;
    }

    const assetIdNum = assetData.AssetId;
    const groupKey = assetIdNum;

    if (!acc[groupKey]) {
      acc[groupKey] = {
        assetId: assetIdNum,
        assetNumber: "",
        assetLocation: undefined, // this will be enriched from the Asset API call
        assetLease: undefined, // this will be enriched from the Asset API call
        status: undefined, // this will be enriched from the Asset API call
        vendor: undefined, //this will be enriched from the Asset API call
        events: [],
        totalDTHours: 0,
        mechanicalDTHours: 0,
        nonMechanicalDTHours: 0,
      };
    }

    // Safety check for DowntimeEvents array
    if (!Array.isArray(assetData.DowntimeEvents)) {
      return acc;
    }

    // Process each event for this asset
    assetData.DowntimeEvents.forEach((event) => {
      // Safety check for event
      if (!event || typeof event !== "object") {
        return;
      }

      // Determine if event is open (has reportingDate but no closingDate)
      const isOpenEvent = event.ReportingDate && !event.ClosingDate;

      // Calculate downtime hours within the date range
      const dtHours = calculateDowntimeHoursInRange(event, startDate, endDate);

      // Add event to the asset's events array
      acc[groupKey].events.push({
        id: event.UniqueId != null ? String(event.UniqueId) : "",
        // If active, duration is the difference between the current time and the start time
        active: !event.DowntimeHours,
        duration: !event.DowntimeHours
          ? (new Date().getTime()
              - new Date(event.ReportingDate || new Date()).getTime())
            / 1000
            / 60
          : (event.DowntimeHours || 0) * 60, // Convert hours to minutes
        dateTime: new Date(event.ReportingDate || new Date()).toISOString(),
        dateTimeEnd: new Date(event.ClosingDate || new Date()).toISOString(),
        relatedAlarm: event.RelatedAlarm || "",
        dtHours: dtHours,
        mechanical: DowntimeIsMechanical[event.IsMechanical],
      });

      // Add to total downtime hours (dtHours is always a number from calculateDowntimeHoursInRange)
      acc[groupKey].totalDTHours += dtHours;

      // Add to mechanical downtime hours if mechanical
      if (DowntimeIsMechanical[event.IsMechanical]) {
        acc[groupKey].mechanicalDTHours += dtHours;
      }
    });

    return acc;
  }, {});

  // Convert grouped object to array and calculate non-mechanical and unknown downtime hours
  return Object.values(groupedByAsset).map((asset) => {
    // Calculate unknown downtime hours (events where mechanical is null)
    const unknownDTHours = asset.events
      .filter((event) => event.mechanical === null)
      .reduce((sum, event) => sum + event.dtHours, 0);

    return {
      ...asset,
      mechanicalDTHours: asset.mechanicalDTHours,
      nonMechanicalDTHours:
        asset.totalDTHours - asset.mechanicalDTHours - unknownDTHours,
      unknownDTHours,
      eventCount: asset.events.length,
    };
  });
};

/**
 * Sort events by status and time
 * @param {Array} events - Array of events to sort
 * @param {Function} isEventActive - Function to check if event is active
 * @returns {Array} Sorted events array
 */
export const sortEventsByStatusAndTime = (events, isEventActive) => {
  if (!Array.isArray(events)) return [];

  return [...events].sort((a, b) => {
    const aIsActive = isEventActive(a);
    const bIsActive = isEventActive(b);

    // If one is active and one is not, active comes first
    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;

    // If both have same status, sort by start time (most recent first)
    return new Date(b.startTime) - new Date(a.startTime);
  });
};

/**
 * Calculate downtime hours from filtered events
 * @param {Array} events - Array of downtime events
 * @returns {Object} Object containing calculated downtime hours
 */
export const calculateDowntimeHours = (events) => {
  if (!Array.isArray(events)) {
    return {
      totalDTHours: 0,
      mechanicalDTHours: 0,
      nonMechanicalDTHours: 0,
      unknownDTHours: 0,
    };
  }

  const totalDTHours = events.reduce(
    (sum, event) => sum + (event.dtHours || 0),
    0
  );
  const mechanicalDTHours = events
    .filter((event) => event.mechanical === true)
    .reduce((sum, event) => sum + (event.dtHours || 0), 0);
  const unknownDTHours = events
    .filter((event) => event.mechanical === null)
    .reduce((sum, event) => sum + (event.dtHours || 0), 0);
  const nonMechanicalDTHours = events
    .filter((event) => event.mechanical === false)
    .reduce((sum, event) => sum + (event.dtHours || 0), 0);

  return {
    totalDTHours,
    mechanicalDTHours,
    nonMechanicalDTHours,
    unknownDTHours,
  };
};

/**
 * Resolve reporting window for downtime overview / detail views.
 * @param {string} selectedDateRange
 * @param {string} customStartDate - YYYY-MM-DD
 * @param {string} customEndDate - YYYY-MM-DD
 * @returns {{ startDate: Date, endDate: Date, statsRange: string, hasCustomBounds: boolean }}
 */
export function getDowntimeReportingRange(
  selectedDateRange,
  customStartDate,
  customEndDate
) {
  const hasCustomBounds =
    selectedDateRange === "custom" && customStartDate && customEndDate;

  if (hasCustomBounds) {
    return {
      startDate: new Date(customStartDate + "T00:00:00.000Z"),
      endDate: new Date(customEndDate + "T23:59:59.999Z"),
      statsRange: "custom",
      hasCustomBounds: true,
    };
  }

  const statsRange =
    selectedDateRange && selectedDateRange !== "custom"
      ? selectedDateRange
      : "30d";
  const endDate = new Date();

  return {
    startDate: getDateOffset(endDate.toISOString(), statsRange),
    endDate,
    statsRange,
    hasCustomBounds: false,
  };
}

/**
 * Filter downtime events that overlap a reporting window.
 * @param {Array} events
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Array}
 */
export function filterDowntimeEventsByRange(events, startDate, endDate) {
  if (!Array.isArray(events)) return [];

  return events.filter((event) => {
    const eventStart = new Date(event.dateTime);
    const eventEnd = event.active
      ? null
      : event.dateTimeEnd
        ? new Date(event.dateTimeEnd)
        : null;

    return (
      eventStart <= endDate && (eventEnd === null || eventEnd >= startDate)
    );
  });
}

/**
 * Calculate availability percentage from downtime hours and date range.
 * @param {number} downtimeHours
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {number}
 */
export function calculateAvailabilityPercentage(
  downtimeHours,
  startDate,
  endDate
) {
  if (!startDate || !endDate) return 0;

  const totalHours =
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
  if (totalHours <= 0) return 0;

  return Math.min(
    100,
    Math.max(0, 100 - (downtimeHours / totalHours) * 100)
  );
}

/** @type {typeof calculateAvailabilityPercentage} */
export const calculateRuntimePercentage = calculateAvailabilityPercentage;

/** @type {typeof calculateAvailabilityPercentage} */
export const calculateMechanicalAvailabilityPercentage =
  calculateAvailabilityPercentage;
